import React, { useState, useEffect } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import Input from "@/components/ui/input";
import Button from "@/components/ui/button";
import Dropdown from "@/components/ui/dropdown";
import Textarea from "@/components/ui/textarea";
import DatePicker from "@/components/ui/datepicker";
import FileInput from "@/components/ui/file-input";
import { parseDate, formatDate, isoToFormDate } from "../utils/dateUtils";
import { getFileDownloadUrl } from "@/lib/files.api";
import type { AnnouncementFormValues } from "../types/announcement-form.types";
import { fetchDepartments } from "../services/departments.api";
import { announcementsApi } from "../services/announcements.api";
import styles from "./CreateAnnouncementDrawer.module.scss";

/** Hedef kitle: "Tüm Personel" seçildiğinde kullanılan özel value (targetAllStaff = true) */
export const TARGET_AUDIENCE_ALL = "__ALL__";

const emptyForm: AnnouncementFormValues = {
  title: "",
  description: "",
  targetAudience: "",
  startDate: "",
  endDate: "",
  imageFile: null,
  imageFileID: null,
};

export interface EditAnnouncementContext {
  editId: string;
  existingImageFileID?: string | null;
}

interface CreateAnnouncementDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  editId?: string | null;
  onSubmit: (
    data: AnnouncementFormValues,
    asDraft?: boolean,
    editContext?: EditAnnouncementContext,
  ) => void | Promise<void>;
}

const CreateAnnouncementDrawer: React.FC<CreateAnnouncementDrawerProps> = ({
  isOpen,
  onClose,
  editId = null,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<AnnouncementFormValues>(emptyForm);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [targetAudienceOptions, setTargetAudienceOptions] = useState<
    { label: string; value: string }[]
  >([{ label: i18n.t("announcementsCommon.allPersonnel"), value: TARGET_AUDIENCE_ALL }]);
  const [targetAudienceLoading, setTargetAudienceLoading] = useState(false);
  const [existingImageFileID, setExistingImageFileID] = useState<string | null>(
    null,
  );
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [uploadedImageFileID, setUploadedImageFileID] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen) {
      setForm(emptyForm);
      setImageFiles([]);
      setExistingImageFileID(null);
      setExistingImageUrl(null);
      setUploadedImageFileID(null);
      return;
    }
    setTargetAudienceLoading(true);
    fetchDepartments({ page: 1, limit: 100 })
      .then((items) => {
        const options: { label: string; value: string }[] = [
          { label: i18n.t("announcementsCommon.allPersonnel"), value: TARGET_AUDIENCE_ALL },
          ...items.map((d) => ({ label: d.name, value: d.id })),
        ];
        setTargetAudienceOptions(options);
      })
      .catch(() => {
        setTargetAudienceOptions([
          { label: i18n.t("announcementsCommon.allPersonnel"), value: TARGET_AUDIENCE_ALL },
        ]);
      })
      .finally(() => {
        setTargetAudienceLoading(false);
      });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !editId) return;
    let cancelled = false;
    setEditLoading(true);
    setExistingImageUrl(null);
    announcementsApi
      .getById(editId)
      .then((ann) => {
        if (cancelled) return;
        const targetAudience = ann.targetAllStaff
          ? TARGET_AUDIENCE_ALL
          : (ann.departments?.[0]?.id ?? "");
        setForm({
          title: ann.title ?? "",
          description: ann.description ?? "",
          targetAudience,
          startDate: isoToFormDate(ann.startDate),
          endDate: isoToFormDate(ann.endDate),
          imageFile: null,
          imageFileID: ann.imageFileID ?? null,
        });
        setExistingImageFileID(ann.imageFileID ?? null);
        if (ann.imageFileID) {
          getFileDownloadUrl(ann.imageFileID)
            .then((url) => {
              if (!cancelled) setExistingImageUrl(url);
            })
            .catch(() => {
              if (!cancelled) setExistingImageUrl(null);
            });
        } else {
          setExistingImageUrl(null);
        }
      })
      .catch((err) => {
        if (!cancelled) console.error(i18n.t("announcementsCommon.loadFailed"), err);
      })
      .finally(() => {
        if (!cancelled) setEditLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, editId]);

  const handleChange = (field: keyof AnnouncementFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTargetAudienceSelect = (value: string | string[]) => {
    const v = Array.isArray(value) ? value[0] : value;
    setForm((prev) => ({ ...prev, targetAudience: v ?? "" }));
  };

  const handleStartDateChange = (
    value: Date | [Date | null, Date | null] | null,
  ) => {
    const date = Array.isArray(value) ? (value[0] ?? null) : value;
    setForm((prev) => ({ ...prev, startDate: formatDate(date) }));
  };

  const handleEndDateChange = (
    value: Date | [Date | null, Date | null] | null,
  ) => {
    const date: Date | null = Array.isArray(value)
      ? (value[1] ?? value[0] ?? null)
      : value;
    setForm((prev) => ({ ...prev, endDate: formatDate(date) }));
  };

  const handleFileSelect = (files: File[]) => {
    setImageFiles(files);
    setForm((prev) => ({
      ...prev,
      imageFile: files[0] ?? null,
    }));
  };

  const handleImageUploadSuccess = (fileId: string) => {
    setUploadedImageFileID(fileId);
    setForm((prev) => ({
      ...prev,
      imageFile: null,
      imageFileID: fileId,
    }));
  };

  const handleImageRemove = () => {
    setUploadedImageFileID(null);
    setForm((prev) => ({
      ...prev,
      imageFile: null,
      imageFileID: null,
    }));
  };

  const handlePublish = () => {
    const payload: AnnouncementFormValues = {
      ...form,
      imageFile: imageFiles[0] ?? null,
      imageFileID: uploadedImageFileID ?? form.imageFileID ?? null,
    };
    if (editId) {
      onSubmit(payload, false, {
        editId,
        existingImageFileID: existingImageFileID ?? undefined,
      });
    } else {
      onSubmit(payload, false);
    }
  };

  const handleDraft = () => {
    const payload: AnnouncementFormValues = {
      ...form,
      imageFile: imageFiles[0] ?? null,
      imageFileID: uploadedImageFileID ?? form.imageFileID ?? null,
    };
    if (editId) {
      onSubmit(payload, true, {
        editId,
        existingImageFileID: existingImageFileID ?? undefined,
      });
    } else {
      onSubmit(payload, true);
    }
  };

  const hasTitle = !!form.title.trim();
  const hasTargetAudience =
    form.targetAudience === TARGET_AUDIENCE_ALL || !!form.targetAudience.trim();
  const isFormValid = hasTitle && hasTargetAudience;

  const footer = (
    <div className={styles.footer}>
      <Button
        variant="outlined"
        color="default"
        onClick={handleDraft}
        className={styles.draftButton}
        disabled={!isFormValid || (editId != null && editLoading)}
      >
        {t("announcements.form.draft")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handlePublish}
        disabled={!isFormValid || (editId != null && editLoading)}
      >
        {t("announcements.form.publish")}
      </Button>
    </div>
  );

  const drawerTitle = editId
    ? t("announcements.form.editTitle")
    : t("announcements.form.title");

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={drawerTitle}
      width="561px"
      position="right"
      footer={footer}
    >
      <div className={styles.drawerContent}>
        {editId && editLoading ? (
          <div className={styles.loadingPlaceholder}>
            {t("common.loading")}
          </div>
        ) : (
          <>
            <FileInput
              label={t("announcements.form.imageLabel")}
              placeholder={t("announcements.form.imagePlaceholder")}
              onFileSelect={handleFileSelect}
              onUploadSuccess={handleImageUploadSuccess}
              onFileRemove={() => {
                handleImageRemove();
              }}
              maxFiles={1}
              variant="outlined"
              accept="image/*"
              fileType="image"
              uploadToBackend
              supportedFormats={["JPG", "JPEG", "PNG", "GIF", "WEBP"]}
              existingFileUrl={existingImageUrl ?? undefined}
            />

            <Input
              label={t("announcements.form.titleLabel")}
              placeholder={t("announcements.form.titlePlaceholder")}
              value={form.title}
              maxLength={500}
              onChange={(e) => handleChange("title", e.target.value)}
              variant="outlined"
              required
            />

            <Textarea
              label={t("announcements.form.descriptionLabel")}
              placeholder={t("announcements.form.descriptionPlaceholder")}
              value={form.description}
              maxLength={10000}
              onChange={(e) => handleChange("description", e.target.value)}
              variant="outlined"
              rows={4}
            />

            <Dropdown
              label={t("announcements.form.targetAudienceLabel")}
              placeholder={
                targetAudienceLoading
                  ? t("common.loading")
                  : t("announcements.form.targetAudiencePlaceholder")
              }
              searchable
              options={targetAudienceOptions}
              value={form.targetAudience || null}
              selectedLabel={
                form.targetAudience
                  ? targetAudienceOptions.find(
                      (o) => o.value === form.targetAudience,
                    )?.label
                  : undefined
              }
              onSelect={handleTargetAudienceSelect}
              disabled={targetAudienceLoading}
            />

            <div className={styles.twoColumnRow}>
              <DatePicker
                label={t("announcements.form.startDateLabel")}
                placeholder={t("announcements.form.datePlaceholder")}
                value={parseDate(form.startDate)}
                onChange={handleStartDateChange}
                format="dd.MM.yyyy"
                locale="tr"
                variant="outlined"
              />
              <DatePicker
                label={t("announcements.form.endDateLabel")}
                placeholder={t("announcements.form.datePlaceholder")}
                value={parseDate(form.endDate)}
                onChange={handleEndDateChange}
                format="dd.MM.yyyy"
                locale="tr"
                variant="outlined"
              />
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default CreateAnnouncementDrawer;
